
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useBilling } from '@/hooks/billing/useBilling';
import { useAdminStatus } from '@/hooks/admin/useAdminStatus';
import BillingNavbar from '@/components/billing/BillingNavbar';
import SignatureDialog from '@/components/billing/SignatureDialog';
import WaveSyncStatus from '@/components/billing/WaveSyncStatus';
import { Estimate } from '@/services/billing/billingService';
import { format } from 'date-fns';
import { PenTool, DollarSign, FileText } from 'lucide-react';
import { generateEstimatePDF } from '@/utils/pdfGenerator';

const EstimatesPage: React.FC = () => {
  const { estimates, isLoading, signContract, createInvoice, refreshData } = useBilling();
  const { isAdmin } = useAdminStatus();
  const [selectedEstimate, setSelectedEstimate] = React.useState<Estimate | null>(null);

  const handleSignContract = async (estimateId: string, signatureData: any) => {
    await signContract(estimateId, signatureData);
    setSelectedEstimate(null);
  };

  const handleCreateInvoice = async (estimateId: string) => {
    await createInvoice(estimateId);
  };

  const handleGeneratePDF = (estimate: Estimate) => {
    generateEstimatePDF(estimate);
  };

  const getStatusColor = (status: Estimate['status']) => {
    switch (status) {
      case 'approved': return 'bg-green-500';
      case 'invoiced': return 'bg-blue-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading estimates...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BillingNavbar />
      
      <div className="container mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Estimates</h1>
          <p className="text-gray-600">
            {isAdmin 
              ? 'Review and manage client estimates and proposals'
              : 'View your service estimates and sign contracts'
            }
          </p>
        </div>

        {estimates.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No estimates found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {estimates.map((estimate) => (
              <Card key={estimate.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      Estimate #{estimate.id.slice(-8)}
                    </CardTitle>
                    <Badge className={getStatusColor(estimate.status)}>
                      {estimate.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    Created: {format(new Date(estimate.created_at), 'MMM d, yyyy')}
                  </p>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    <div className="text-2xl font-bold">${estimate.total.toFixed(2)}</div>
                    
                    <div className="space-y-2">
                      {estimate.line_items.map((item, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          {item.description} - {item.quantity} × ${item.rate.toFixed(2)}
                        </div>
                      ))}
                    </div>

                    <WaveSyncStatus
                      syncStatus={estimate.wave_sync_status}
                      syncError={estimate.wave_sync_error}
                      waveId={estimate.wave_estimate_id}
                      itemType="estimate"
                      itemId={estimate.id}
                      onSyncComplete={refreshData}
                    />

                    <div className="flex flex-col space-y-2">
                      <Button
                        variant="outline"
                        onClick={() => handleGeneratePDF(estimate)}
                        className="flex items-center"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>

                      {!isAdmin && estimate.status === 'pending' && (
                        <Button
                          onClick={() => setSelectedEstimate(estimate)}
                          className="flex items-center"
                        >
                          <PenTool className="h-4 w-4 mr-2" />
                          Sign Contract
                        </Button>
                      )}

                      {isAdmin && estimate.status === 'approved' && (
                        <Button
                          onClick={() => handleCreateInvoice(estimate.id)}
                          className="flex items-center"
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          Create Invoice
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {selectedEstimate && (
        <SignatureDialog
          isOpen={!!selectedEstimate}
          onClose={() => setSelectedEstimate(null)}
          onSign={handleSignContract}
          estimateId={selectedEstimate.id}
        />
      )}
    </div>
  );
};

export default EstimatesPage;
